import { inject, Injectable, NgZone } from '@angular/core';
import { ECopyBtnStatusType, EVoiceBtnStatusType } from '@type/enums/common';
import { EAudioPlayStatusType } from '@type/enums/mobile';
import { IUpdateDiaReq } from '@type/request/reqDia';
import Compressor from 'compressorjs';
import dayjs from 'dayjs';
import TurndownService from 'turndown';
import { AiApiService, getUrl } from '../api/ai-api.service';
import { AI_EXPIRE_DAYS, MARKED_NULL_REPLY, NOT_REPLY_BY_CANCEL_TIP, NOT_REPLY_INFO } from '../constants/dialogue';
import { NO_LOGIN_KEY } from '../constants/storageKey';
import { DiaCommonService } from '../pages/dialogue/dia-common.service';
import { EAiChatRoleType, EAiReqUserType } from '../types/enums/aiEnum';
import { IGroupResponseData, IMesRecord, IMesRecordGroup, IUploadPic } from '../types/mesRecords';
import { IAiAgentV2Req, IAiV2MesList } from '../types/request/reqAI';
import { IUserInfo } from '../types/response/resUser';
import { getAiAgentResResults } from '../utils/useAIResData';
import { findPuncSpanCon, findPunctuationIndex } from '../utils/useRegExp';
import { useUUID } from '../utils/useUUID';
import { AudioControllerService } from './audio-controller.service';
import { MesRecordsService } from './mes-records.service';
import { TipPopService } from './tip-pop.service';
import { UserCommonService } from './user-common.service';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private tp = inject(TipPopService)
  private aiApiServe = inject(AiApiService)
  private userCSer = inject(UserCommonService)
  private diaComServe = inject(DiaCommonService)
  private mesRecordsServe = inject(MesRecordsService)
  // markdown语法处理
  private turndownServe = new TurndownService()
  private nz = inject(NgZone)

  // 滚动底部按钮状态
  public toBottomBtnStatus: boolean = false
  // 语音自动朗读功能状态
  public defaultVolumnStatus: EVoiceBtnStatusType = EVoiceBtnStatusType.OFF

  // 显示复制/成功按钮图标
  public copyOrSuc: ECopyBtnStatusType = ECopyBtnStatusType.COPY
  // 音频合成实例对象
  public audioController = new AudioControllerService()
  // 是否在生成文本内容
  public isGenerateReplyText: boolean = false
  // 判断当前文本内容是否完成输出（呈现消息底部按钮）
  public isReplyTextOutputFinish: boolean = true
  // 页面卸载前是否传入返回内容
  public unloadTextStatus: boolean = true
  // 语音合成间隔时间
  public voiceBtnStartTime: Date = new Date()
  // 当前对话是产生新的回复消息内容（用于判断当前是否产生过新的对话）
  public isGenNewReplyMesStatus: boolean = false

  // 追问问题推荐
  public recommendPros: string[] = ['不同体检套餐间有什么区别呢，比较推荐我购买哪个？', '如何帮家人预约？', '计划有变、如何更改预约？']

  // 用户发送消息
  private userSendMes: string = ''
  // 用户上传图片
  public viewPicFiles: { id: string, name: string, url: string }[] = []
  public picFiles: string[] = []
  // 消息类型为添加/修改（True表示添加）
  private isSendNewReply: boolean = true
  // 判断当前是否正在朗读内容
  private isSpeechingStatus: boolean = false
  // 上传图片时标记当前界面容器
  private curContainer: HTMLDivElement | null = null

  public aiController: AbortController | null = null

  // 当前对话
  get curDialogue() {
    return this.mesRecordsServe.mesRecords[this.mesRecordsServe.gid]
  }

  // 当前对话记录
  get curRecords() {
    return this.curDialogue.records
  }

  constructor() {
    // 绑定浏览器卸载前的事件，发送当时已有的文本至服务器
    window.addEventListener('beforeunload', () => {
      this.isReplyTextOutputFinish = true
      if (!this.unloadTextStatus) {
        const mesRecords = this.curRecords
        const content = mesRecords[mesRecords.length - 1].content
        let url = ''
        let data = ''

        if (!this.isSendNewReply) {
          url = getUrl('ai_context/update')
          data = JSON.stringify({ id: mesRecords[mesRecords.length - 1].id, content })
        } else {
          url = getUrl('ai_context/create')
          data = JSON.stringify({
            key: this.curDialogue.key,
            role: EAiChatRoleType.AI,
            content: content ? content : NOT_REPLY_INFO,
            image_url: ''
          })
        }
        this.unloadTextStatus = true
        navigator.sendBeacon(url, data)
      }
    })

    // 绑定应用切换或熄屏事件
    document.addEventListener('visibilitychange', () => {
      document.hidden && this.silenceVoiceStatus()
    })
  }

  // ---------------------------------- 语音 ----------------------------------

  // 朗读整条对话
  public async speechLastMesCon(e?: TouchEvent) {
    if (e && !this.diaComServe.handleTouchUnintendedAct(e)) { return }

    let endTime = new Date()
    if (endTime.getTime() - this.voiceBtnStartTime.getTime() <= 1000) { return }
    this.voiceBtnStartTime = endTime

    await new Promise<void>((resolve) => {
      setTimeout(() => {
        this.audioController.volumnStatus = EAudioPlayStatusType.ON
        this.isSpeechingStatus = false
        resolve()
      }, 800)
    })

    if (!this.audioController.audio.paused || this.audioController.isReading) {
      this.silenceVoiceStatus()
      return
    }

    this.silenceVoiceStatus()
    this.audioController.volumnStatus = EAudioPlayStatusType.READING
    let speechTexts = (this.turndownServe.turndown(this.curRecords[this.curRecords.length - 1].content)).split(/[。？！，：]/)

    this.audioController.start()
    this.audioController.lastReplyTextIsFinish = false
    this.isSpeechingStatus = true

    for (let i = 0; i < speechTexts.length && this.isSpeechingStatus; i++) {
      speechTexts[i].trim() && this.audioController.addUrl(await this._getSpeakSynUrl(speechTexts[i]))
    }
    this.audioController.lastReplyTextIsFinish = true
  }

  // 清除当前流式文本语音/整体语音状态
  public silenceVoiceStatus(flag: boolean = true) {
    this.audioController.audio.pause()
    this.audioController.urls = []
    this.audioController.end()
    this.audioController.audio.src = ''
    flag && (this.audioController.audio = new Audio())
    this.audioController.lastReplyTextIsFinish = true
    this.isSpeechingStatus = false
    this.audioController.volumnStatus = EAudioPlayStatusType.ON
    this.audioController.isReading = false
  }

  // 语音状态切换
  public changeVoiceBtnStatus() {
    this.defaultVolumnStatus =
      this.defaultVolumnStatus === EVoiceBtnStatusType.ON ? EVoiceBtnStatusType.OFF : EVoiceBtnStatusType.ON
    // 静音状态时取消当前语音播放
    if (this.defaultVolumnStatus === EVoiceBtnStatusType.OFF) {
      this.isSpeechingStatus = false
      // 文本未展示完成由右上角音量按钮控制取消，展示完成后通过文本底部的音量按钮控制取消
      !this.isReplyTextOutputFinish && this.silenceVoiceStatus()
    }
  }

  // 返回语音合成url
  private async _getSpeakSynUrl(con: string) {
    const resToken = await this.aiApiServe.getSpeechSynAccessToken()

    // 文本内容合成音频
    const res = await this.aiApiServe.getSpeechSynthesis(con, resToken)
    return URL.createObjectURL(res as unknown as Blob)
  }

  // ---------------------------------- 文本生成与消息发送 ----------------------------------

  // 重新生成文本
  public refreshReplyText(container: HTMLDivElement, e?: TouchEvent) {
    if (e && !this.diaComServe.handleTouchUnintendedAct(e)) { return }

    this.isSendNewReply = false
    this.silenceVoiceStatus(false)
    this._replyMes(container).then(() => { this.scrollToPageBottom(container) })
  }

  // 用户发送消息
  public async sendMes(content: string, container: HTMLDivElement) {
    this.isGenerateReplyText = true

    // 处理容器
    container = this.curContainer ? this.curContainer : container
    this.curContainer = null
    // 处理文本信息
    this.userSendMes = content.trim()
    // 处理图片
    if (this.viewPicFiles.length) {
      this.picFiles = this.viewPicFiles.filter(pic => pic.url).map(pic => pic.url)
      this.viewPicFiles = []
    }
    // 设置发送状态
    this.isSendNewReply = true

    await this._handleUserMesToContext(this.userSendMes, container, this.picFiles)
    this._replyMes(container).then(() => { this.scrollToPageBottom(container, 150) })
  }

  // 处理用户消息至上下文
  private async _handleUserMesToContext(content: string, container: HTMLDivElement, images: string[]) {
    // 发送内容处理
    const sendMes: IMesRecord[] = []
    if (images.length) {
      for (const img of images) {
        sendMes.push(this._generateUserMesParams('[图片消息]', img))
      }
    }
    sendMes.push(this._generateUserMesParams(content))

    await this.nz.run(async () => {
      this.curRecords.push(...sendMes)
      this.scrollToPageBottom(container)

      // 存储用户消息
      const res: IGroupResponseData = await this.aiApiServe.insertMesRecordByGroup(
        sendMes.map(mes => ({ key: this.curDialogue.key, role: EAiChatRoleType.USER, content: mes.content, image_url: mes.imageUrl, data: '' }))
      )
      if (res.code === 200 && res.data && res.data.length) {
        const resIds = res.data.map(d => d.id)
        sendMes.forEach((mes, index) => { mes.id = resIds[index] })

        const params: IUpdateDiaReq = { id: this.curDialogue.did as number }
        this.curDialogue.session_id && (params.session_id = this.curDialogue.session_id)
        this.mesRecordsServe.updateDia(params)
      }
    })
  }

  // 生成用户消息存储上下文参数
  private _generateUserMesParams(content: string = '', imageUrl: string = ''): IMesRecord {
    return { role: EAiChatRoleType.USER, time: new Date(), content, imageUrl, items: [], id: -1 }
  }

  // 获取上一条用户发送的所有数据消息（文本/文本+图片）
  private _refreshLastUserSendMes() {
    this._resetLocalMesData()

    const M = this.curRecords
    this.userSendMes = M[M.length - 2].content
    for (let i = 3; i <= 5; i++) {
      const curM = M[M.length - i]
      if (curM) {
        if (curM.role === EAiChatRoleType.AI) { return }
        else if (curM.imageUrl) { this.picFiles.unshift(curM.imageUrl) }
      }
    }
  }

  // 重置本地消息数据
  private _resetLocalMesData() {
    this.userSendMes = ''
    this.picFiles = []
  }

  // 流式呈现信息
  private _replyMes(container: HTMLDivElement): Promise<void> {
    // 状态变更
    this.isGenerateReplyText = true
    this.isReplyTextOutputFinish = false
    this.unloadTextStatus = false

    // 获取本地对应消息回复数组
    const M = this.curRecords
    // 处理刷新/插入数据
    if (!this.isSendNewReply) {
      M[M.length - 1].content = ''
      M[M.length - 1].items = []
      M[M.length - 1].time = new Date()
      this._refreshLastUserSendMes()
    } else {
      M.push({ role: EAiChatRoleType.AI, time: new Date(), content: '', id: -1, imageUrl: '', items: [] })
    }
    this.isGenNewReplyMesStatus = true
    this.scrollToPageBottom(container, 500)

    return new Promise<void>(async (resolve) => {
      const messageGroup: IAiV2MesList[] = this.picFiles.length ? this.picFiles.map(pic => ({ role: 'customer', message: pic, messageType: 'image' })) : []
      messageGroup.push({ role: 'customer', message: this.userSendMes, messageType: 'text' })

      // 请求参数
      const params: IAiAgentV2Req = {
        sessionId: this._getSessionId(),
        userType: EAiReqUserType.USER,
        aiAnswer: true,
        messageGroup
        // ---------------------------------- 体检报告，带上 healthReport 参数 ----------------------------------
      }
      this._resetLocalMesData()

      // 登录状态加入用户信息
      if (this.userCSer.LOGIN_STATUS) {
        const cont = this.userCSer.curContact as IUserInfo
        params.userInfo = {
          id: cont.id.toString(),
          gender: cont.sex ? (cont.sex === 1 ? '男' : '女') : '未知',
          address: cont.address,
          age: cont.age,
          name: cont.name
        }
      }

      this.aiController = new AbortController()
      // 获取AI智能体回复
      try {
        // 流式数据处理
        const resData = await this.aiApiServe.getAiAgentReplyV2(params, this.aiController!.signal)
        const reader = resData!.getReader()
        const decoder = new TextDecoder('utf-8')
        // 语音合成的文字截取变量
        let voice2text = ''
        // 完整回复内容
        let completedText = ''

        // 语音合成
        this.audioController.lastReplyTextIsFinish = false
        if (this.defaultVolumnStatus === EVoiceBtnStatusType.ON) {
          this.isSpeechingStatus = true
          this.audioController.start()
        }

        // 数据处理
        while (1) {
          // 流式数据结束标记与值
          const { done, value } = await reader.read()
          // 数据拼接
          let data = ''

          // 处理大块流式数据分块
          const chunks = this._splitBuffer(value, 256)
          for (const chunk of chunks) {
            const text = decoder.decode(new Uint8Array(chunk), { stream: true })
            data += text
          }

          // 数据格式化
          const { result, txtFinish, finish, session_id, commodity } = getAiAgentResResults(data)

          //  当文本内容返回完成后，处理返回的商品内容
          if (commodity.length) {
            M[M.length - 1].items = commodity
            this.scrollToPageBottom(container, 150)
          }

          // 文本全部生成完成后更新生成状态
          txtFinish && (this.nz.run(() => { this.isReplyTextOutputFinish = true }))
          // 对首次返回的对应不同的session_id进行存储
          session_id && this._handleStoreFirstSessionId(session_id)

          // 判断文本是否生成完成或是否还有文本内容，将文本进行推入MES
          if (!txtFinish || result) {
            // 处理文本内容
            completedText += result
            await this.nz.run(async () => {
              // 处理返回的文本内容
              M[M.length - 1].content = completedText
                .replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n/g, '<br>')
              if (container.clientHeight + container.scrollTop >= container.scrollHeight - 150) {
                this.scrollToPageBottom(container, 0, 'instant')
              }
            })
          }

          // 处理音频内容，判断当前result中是否有 ，、。！？：；等符号，进行截断与拼接
          if (this.defaultVolumnStatus === EVoiceBtnStatusType.ON) {
            if (!txtFinish) {
              const divideIndex = findPunctuationIndex(result)
              let residueText: string = ''
              //判断当前是否有截断标志符号
              if (divideIndex === -1) {
                voice2text += result
              } else {
                // 将标志符号前的内容进行拼接，后面的内容留至下一次拼接
                voice2text += result.substring(0, divideIndex + 1)
                residueText = result.substring(divideIndex + 1)
                voice2text = findPuncSpanCon(voice2text)
                this.audioController.addUrl(await this._getSpeakSynUrl(voice2text))
                voice2text = residueText
              }
            }
            if (txtFinish && (result || voice2text)) {
              voice2text += findPuncSpanCon(result)
              this.audioController.addUrl(await this._getSpeakSynUrl(voice2text))
              voice2text = ''
            }
          }

          // 返回消息处理结束
          if (done || !this.isGenerateReplyText || finish) {
            this._handleStopAIMesReply(container)
            resolve()
            break
          }
        }
      } catch (error: any) {
        // console.log(error)
        // 用户手动取消生成回复
        this._handleStopAIMesReply(container, true)
      }
    })
  }

  // 校验会话 session_id 时效
  private _getSessionId() {
    const sessionId = this.curDialogue.session_id

    if (this._isSessionIdExpired() || !sessionId) {
      this.curDialogue.session_id = ''
      return ''
    }

    return sessionId
  }

  // 流式返回大块数据进行切割处理
  private _splitBuffer(buffer: any, chunkSize: number) {
    if (!buffer) { return [] }
    const chunks = []
    for (let i = 0; i < buffer.length; i += chunkSize) {
      chunks.push(buffer.slice(i, i + chunkSize))
    }
    return chunks
  }

  // 停止生成消息回复（若还在加载中则生成“回复停止生成”）
  private async _handleStopAIMesReply(container: HTMLDivElement, manualCancel: boolean = false) {
    // 处理文本内容为空时
    const lastMes = this.curRecords[this.curRecords.length - 1]
    const imgUrl = this.curRecords[this.curRecords.length - 2].imageUrl
    if (!lastMes.content) {
      lastMes.content = (imgUrl && !manualCancel) ? MARKED_NULL_REPLY : NOT_REPLY_BY_CANCEL_TIP
      this.scrollToPageBottom(container, 100)
    }

    // 处理数据
    await this._handleStoreAIMesByType()
    this.unloadTextStatus = true
    this.aiController = null
    this.isGenerateReplyText = false
    this.audioController.lastReplyTextIsFinish = this.isReplyTextOutputFinish = true
    this.scrollToPageBottom(container, 150)
  }

  // 根据类型刷新或添加AI返回消息
  private async _handleStoreAIMesByType() {
    const R = this.curRecords[this.curRecords.length - 1]
    if (!this.isSendNewReply) {
      await this.aiApiServe.updateMesRecord(R.id, R.content, JSON.stringify(R.items))
    } else {
      const res: IGroupResponseData =
        await this.aiApiServe.insertMesRecordByGroup([{ key: this.curDialogue.key, role: EAiChatRoleType.AI, content: R.content, image_url: '', data: JSON.stringify(R.items) }])
      if (res.code && res.data && res.data.length) { R.id = res.data[0].id }
    }
    this.mesRecordsServe.updateDia({ id: this.curDialogue.did as number, session_id: this.curDialogue.session_id })
  }

  // 存储首次返回的消息sessionId
  private _handleStoreFirstSessionId(session_id: string) {
    if (!this.curDialogue.session_id) {
      this.curDialogue.session_id = session_id
      this._isSessionIdExpired() && (this.curDialogue.create_time = new Date())
      this.mesRecordsServe.updateDia({
        id: this.curDialogue.did as number,
        session_id,
        session_create_time: dayjs(this.curDialogue.create_time).format('YYYY-MM-DD HH:mm:ss')
      })
    }
  }

  // 判断当前会话 session_id 是否过期
  private _isSessionIdExpired() {
    const sessionTime = dayjs(this.curDialogue.create_time)
    const now = dayjs()
    return now.diff(sessionTime, 'second') > AI_EXPIRE_DAYS
  }

  // ---------------------------------- 界面控制 ----------------------------------

  // 指定元素界面划动至底部
  public scrollToPageBottom(container: HTMLDivElement, delayTime: number = 100, behavior: ScrollBehavior = 'smooth') {
    setTimeout(() => {
      container.scrollTo({ top: container.scrollHeight + container.clientHeight, behavior })
    }, delayTime)
  }

  // 修改滚动底部按钮状态
  public changeToBottomBtnStatus(container: HTMLDivElement) {
    this.toBottomBtnStatus = (container.clientHeight + container.scrollTop <= container.scrollHeight - 50)
  }

  // ---------------------------------- 基础功能 ----------------------------------

  // 复制
  public copyReplyCon(status: ECopyBtnStatusType, e?: TouchEvent) {
    if ((e && !this.diaComServe.handleTouchUnintendedAct(e))
      || status === ECopyBtnStatusType.SUCCESS) { return }

    navigator.clipboard.writeText(
      this.turndownServe.turndown(this.curRecords[this.curRecords.length - 1].content)
    ).then(() => {
      this.tp.tipPop('已复制')
      this.copyOrSuc = ECopyBtnStatusType.SUCCESS
      setTimeout(() => {
        this.copyOrSuc = ECopyBtnStatusType.COPY
      }, 2000)
    })
  }

  // ---------------------------------- 图片 ----------------------------------

  // 图片上传
  public emitUploadPic(e?: TouchEvent) {
    if (e && !this.diaComServe.handleTouchUnintendedAct(e)) { return }
    document.getElementById('upload-pic')!.click()
  }

  // 上传图片解析
  public async sendPicAnalysis(e: Event, container: HTMLDivElement) {
    // 限制最多只可上传3张照片
    if (this.viewPicFiles.length >= 3) {
      this.tp.tipPop('最多上传3张图片', 'warning')
      return
    }

    this.tp.tipPop('图片上传中', 'loading')

    const fileInput = e.target as HTMLInputElement
    const picFIle = fileInput.files

    if (picFIle && picFIle.length > 0) {
      const image = picFIle[0]
      // 图片压缩
      new Compressor(image, {
        quality: .8,
        maxWidth: 1920,
        maxHeight: 1080,
        success: async (result) => {
          const imageFile = new File([result], image.name, { type: image.type })
          const formData = new FormData()
          formData.append('file', imageFile)
          // console.log(imageFile.size / 1024 / 1024)

          // 返回图片地址
          const picPath: IUploadPic = await this.aiApiServe.uploadPic(formData)
          const picUrl = picPath?.data?.full_url ?? ''
          this.viewPicFiles.push({ id: useUUID(0, 8), name: image.name, url: picUrl })
          this.nz.run(() => {
            this.tp.tipPop(`图片上传${picUrl ? '成功' : '失败'}`, `${picUrl ? 'success' : 'warning'}`)
          })
          this.curContainer = container
        },
        error(err) { console.log(err.message) }
      })
    }
  }

  // 删除已上传图片
  public deleteUploadPic(id: string, e?: TouchEvent) {
    if (e && !this.diaComServe.handleTouchUnintendedAct(e)) { return }
    this.viewPicFiles = this.viewPicFiles.filter(pic => pic.id !== id)
  }

  // ---------------------------------- 会话 ----------------------------------

  // 跳转至指定对话
  public skipToAssignDialogue(gid: number, container: HTMLDivElement) {
    const M = this.mesRecordsServe.mesRecords[gid]

    this.mesRecordsServe.gid = gid
    M.loading_record_num = M.records.length
    this.scrollToPageBottom(container, 50, 'instant')

    // 登录状态下切换联系人
    if (this.userCSer.LOGIN_STATUS) {
      this.userCSer.curContact = this.userCSer.contsInfo!.filter(cont => cont.id === M.user_id)[0]
    }
  }

  // 添加新的对话内容
  public async createDialogueMes() {
    const key = useUUID(0, 16)
    const noLoginKey = localStorage.getItem(NO_LOGIN_KEY)
    const id = this.userCSer.LOGIN_STATUS ? this.userCSer.userInfo?.wx_user_id : noLoginKey

    const did = await this.mesRecordsServe.createDiaList(this.userCSer.LOGIN_STATUS, id as (number | string), key, 'default')
    this.mesRecordsServe.addOriginalMesList({
      key,
      did,
      wxid: id as number,
      user_id: this.userCSer.curDefContId(),
      no_login_key: noLoginKey ?? '',
      direc: 'front'
    })
  }

  // 聊天记录第一条回复内容
  public getFirstRepltMes(dia: IMesRecordGroup) {
    const getMes = (mes: string) => {
      return this.turndownServe.turndown(mes).substring(0, 30)
    }

    return dia.first_reply ? getMes(dia.first_reply) : (dia.records.length > 1 ? getMes(dia.records[1].content) : '')
  }

}